import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import { PageHero } from "@/components/base/PageHero";
import FaqAccordion from "@/components/base/FaqAccordion";
import { useSeo } from "@/hooks/useSeo";
import { faqCategories, faqList } from "@/mocks/faq";
import { siteBrand } from "@/mocks/site";

const faqSchema = [
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqList.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
    inLanguage: "zh-CN",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://hongshanyang.cn/" },
      { "@type": "ListItem", position: 2, name: "常见问题", item: "https://hongshanyang.cn/faq" },
    ],
  },
];

export default function FaqPage() {
  const [active, setActive] = useState(faqCategories[0]);

  const filtered = useMemo(
    () => (active === faqCategories[0] ? faqList : faqList.filter((item) => item.category === active)),
    [active],
  );

  useSeo({
    title: "常见问题｜独家爆料投稿的匿名、安全与核实说明",
    description:
      "关于独家爆料投稿的高频问答：回应时间、如何匿名提交、是否会记录 IP、材料如何脱敏、线索未被采用后的处理方式，一次说清楚。",
    keywords: "独家爆料常见问题,匿名爆料安全,投稿回应时间,线索脱敏,爆料材料要求",
    path: "/faq",
    schema: faqSchema,
  });

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full flex-1">
        <PageHero
          eyebrow="FAQ · 常见问题"
          title="你可能正在犹豫的那些问题"
          description="我们把投稿者最常问的问题整理在这里。如果下面没有你想问的，可以直接写信给我们，问题本身不会暴露你的身份。"
          crumbs={[{ label: "首页", to: "/" }, { label: "常见问题" }]}
          image="https://readdy.ai/api/search-image?query=abstract%20dark%20vertical%20paper%20strips%20arranged%20in%20a%20row%20with%20soft%20warm%20backlight%2C%20deep%20charcoal%20background%2C%20crimson%20accent%20edge%20glow%2C%20minimalist%20cinematic%20still%20life%2C%20fine%20film%20grain%2C%20no%20text&width=1920&height=900&seq=anxian-faq-hero&orientation=landscape"
          imageAlt="暖色背光的竖向纸条排列，代表常见问题与说明文件"
          meta={
            <div className="flex flex-wrap gap-3">
              {["提交相关", "匿名与安全", "材料与核实", "后续跟进"].map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-background-50/20 bg-background-50/5 px-3.5 py-1.5 text-xs text-background-100"
                >
                  {tag}
                </span>
              ))}
            </div>
          }
        />

        <section className="w-full bg-background-50 py-16 md:py-24">
          <div className="mx-auto w-full max-w-[1320px] px-5 md:px-8">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] lg:gap-16">
              <div className="lg:sticky lg:top-28 lg:self-start">
                <h2 className="font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
                  共 {faqList.length} 个问题
                </h2>
                <p className="mt-4 text-sm leading-7 text-foreground-600">
                  按主题筛选，或直接往下翻。所有回答都对应我们在实际核实流程中的做法，而不是原则口号。
                </p>

                <div className="mt-7 flex flex-wrap gap-2">
                  {faqCategories.map((category) => (
                    <button
                      key={category}
                      type="button"
                      onClick={() => setActive(category)}
                      className={`cursor-pointer whitespace-nowrap rounded-full border px-4 py-2 text-xs transition-colors ${
                        active === category
                          ? "border-primary-300 bg-primary-500 text-background-50"
                          : "border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>

                <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
                  <h3 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-950">
                    <i className="ri-mail-lock-line text-accent-600"></i>
                    没找到答案？
                  </h3>
                  <p className="mt-2 text-xs leading-6 text-foreground-600">
                    把你的疑问发到加密邮箱，我们同样会在 72 小时内回复，提问不需要附带你自己的任何信息。
                  </p>
                  <a
                    href={`mailto:${siteBrand.email}`}
                    className="mt-3 inline-flex break-all text-xs font-medium text-primary-600 hover:text-primary-700"
                  >
                    {siteBrand.email}
                  </a>
                </div>
              </div>

              <div>
                <FaqAccordion key={active} items={filtered} defaultOpen={0} />
                <p className="mt-6 text-xs text-foreground-500">
                  当前显示 {filtered.length} 条 · 分类：{active}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-16 md:py-20">
          <div className="mx-auto flex w-full max-w-[1320px] flex-col items-start gap-6 px-5 md:flex-row md:items-center md:justify-between md:px-8">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                问题问完了，线索还在手里
              </h2>
              <p className="mt-3 max-w-xl text-sm leading-7 text-foreground-600">
                投递表只需几分钟。你可以在表格里只写摘要，剩下的细节我们之后再一起补。
              </p>
            </div>
            <Link
              to="/submit"
              className="inline-flex shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-shield-keyhole-line text-base"></i>
              提交线索
            </Link>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}